export interface CountryInfo {
  countryCode: string;
  countryName: string;
  isChina: boolean;
  region?: string;
  city?: string;
}

export interface CountryDetectionOptions {
  timeout?: number;
  fallbackToTimezone?: boolean;
  fallbackToLanguage?: boolean;
}

/** Mainland China only (CN/CHN). HK, MO, TW excluded — CU-86c948jd8 */
const CHINA_CODES = ['CN', 'CHN'];
const DEFAULT_OPTIONS: CountryDetectionOptions = {
  timeout: 5000,
  fallbackToTimezone: true,
  fallbackToLanguage: true,
};


let cachedCountryInfo: CountryInfo | null = null;

/**
 * Detect user's country using multiple fallback methods
 */
export async function detectCountry(options: CountryDetectionOptions = {}): Promise<CountryInfo> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  
  if (cachedCountryInfo) {
    return cachedCountryInfo;
  }

  try {
    
    let countryInfo = await detectFromIpServices(opts.timeout);
    if (countryInfo) {
      cachedCountryInfo = countryInfo;
      return countryInfo;
    }

    
    if (opts.fallbackToTimezone) {
      countryInfo = detectFromTimezone();
      if (countryInfo) {
        cachedCountryInfo = countryInfo;
        return countryInfo;
      }
    }

    
    if (opts.fallbackToLanguage) {
      countryInfo = detectFromLanguage();
      cachedCountryInfo = countryInfo;
      return countryInfo;
    }

    
    return getDefaultCountryInfo();
  } catch (error) {
    console.warn('Country detection failed:', error);
    const fallback = getDefaultCountryInfo();
    cachedCountryInfo = fallback;
    return fallback;
  }
}

/**
 * Check if current user is likely in China (for map provider selection)
 */
export async function isUserInChina(options?: CountryDetectionOptions): Promise<boolean> {
  const countryInfo = await detectCountry(options);
  return countryInfo.isChina;
}

/**
 * Force refresh the cached country detection
 */
export async function refreshCountryDetection(options?: CountryDetectionOptions): Promise<CountryInfo> {
  cachedCountryInfo = null;
  return detectCountry(options);
}

/**
 * Get cached country info without making new requests
 */
export function getCachedCountryInfo(): CountryInfo | null {
  return cachedCountryInfo;
}

/**
 * Method 1: IP-based country detection using multiple services
 */
async function detectFromIpServices(timeout = 5000): Promise<CountryInfo | null> {
  const services = [
    { name: 'ipapi.co', url: 'https://ipapi.co/json/', parser: parseIpapiResponse },
    { name: 'ip-api.com', url: 'http://ip-api.com/json/', parser: parseIpApiResponse },
    { name: 'ipinfo.io', url: 'https://ipinfo.io/json', parser: parseIpinfoResponse },
  ];

  for (const service of services) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(service.url, {
        signal: controller.signal,
        headers: { 'Accept': 'application/json' },
      });

      clearTimeout(timeoutId);

      if (!response.ok) continue;

      const data = await response.json();
      const countryInfo = service.parser(data);

      if (countryInfo) {
        return countryInfo;
      }
    } catch (error) {
      console.warn(`${service.name} detection failed:`, error);
      continue;
    }
  }

  return null;
}

/**
 * Method 2: Timezone-based country detection
 */
function detectFromTimezone(): CountryInfo | null {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    
    const mainlandChinaTimezones = [
      'Asia/Shanghai',
      'Asia/Beijing',
      'Asia/Chongqing',
      'Asia/Harbin',
      'Asia/Kashgar',
      'Asia/Urumqi',
    ];

    if (mainlandChinaTimezones.includes(timezone)) {
      return {
        countryCode: 'CN',
        countryName: 'China',
        isChina: true,
      };
    }

    
    const timezoneMap: Record<string, Omit<CountryInfo, 'isChina'>> = {
      'America/New_York': { countryCode: 'US', countryName: 'United States' },
      'America/Los_Angeles': { countryCode: 'US', countryName: 'United States' },
      'Europe/London': { countryCode: 'GB', countryName: 'United Kingdom' },
      'Europe/Berlin': { countryCode: 'DE', countryName: 'Germany' },
      'Europe/Paris': { countryCode: 'FR', countryName: 'France' },
      'Asia/Tokyo': { countryCode: 'JP', countryName: 'Japan' },
      'Australia/Sydney': { countryCode: 'AU', countryName: 'Australia' },
    };

    const mapped = timezoneMap[timezone];
    if (mapped) {
      return {
        ...mapped,
        isChina: CHINA_CODES.includes(mapped.countryCode),
      };
    }

    return null;
  } catch (error) {
    console.warn('Timezone-based detection failed:', error);
    return null;
  }
}

/**
 * Method 3: Language-based country detection (least accurate)
 */
function detectFromLanguage(): CountryInfo {
  try {
    const languages = navigator.languages || [navigator.language];
    const primaryLanguage = languages[0] || 'en-US';

    
    if (primaryLanguage.startsWith('zh')) {
      return {
        countryCode: 'CN',
        countryName: 'China',
        isChina: true,
      };
    }

    
    const languageMap: Record<string, Omit<CountryInfo, 'isChina'>> = {
      'en-US': { countryCode: 'US', countryName: 'United States' },
      'en-GB': { countryCode: 'GB', countryName: 'United Kingdom' },
      'de': { countryCode: 'DE', countryName: 'Germany' },
      'fr': { countryCode: 'FR', countryName: 'France' },
      'es': { countryCode: 'ES', countryName: 'Spain' },
      'ja': { countryCode: 'JP', countryName: 'Japan' },
    };

    const mapped = languageMap[primaryLanguage] || languageMap[primaryLanguage.split('-')[0]];
    if (mapped) {
      return {
        ...mapped,
        isChina: CHINA_CODES.includes(mapped.countryCode),
      };
    }
  } catch (error) {
    console.warn('Language-based detection failed:', error);
  }

  return getDefaultCountryInfo();
}

/**
 * Default fallback country info
 */
function getDefaultCountryInfo(): CountryInfo {
  return {
    countryCode: 'US',
    countryName: 'United States',
    isChina: false,
  };
}

/**
 * Parse ipapi.co response
 */
function parseIpapiResponse(data: any): CountryInfo | null {
  if (!data?.country_code) return null;

  const countryCode = data.country_code.toUpperCase();
  return {
    countryCode,
    countryName: data.country_name || data.country || countryCode,
    isChina: CHINA_CODES.includes(countryCode),
    region: data.region,
    city: data.city,
  };
}

/**
 * Parse ip-api.com response
 */
function parseIpApiResponse(data: any): CountryInfo | null {
  if (!data?.countryCode) return null;

  const countryCode = data.countryCode.toUpperCase();
  return {
    countryCode,
    countryName: data.country || countryCode,
    isChina: CHINA_CODES.includes(countryCode),
    region: data.regionName,
    city: data.city,
  };
}

/**
 * Parse ipinfo.io response
 */
function parseIpinfoResponse(data: any): CountryInfo | null {
  if (!data?.country) return null;

  const countryCode = data.country.toUpperCase();
  return {
    countryCode,
    countryName: getCountryNameFromCode(countryCode),
    isChina: CHINA_CODES.includes(countryCode),
    region: data.region,
    city: data.city,
  };
}

/**
 * Get country name from ISO country code
 */
function getCountryNameFromCode(code: string): string {
  const countryNames: Record<string, string> = {
    'CN': 'China',
    'HK': 'Hong Kong',
    'MO': 'Macau',
    'TW': 'Taiwan',
    'US': 'United States',
    'GB': 'United Kingdom',
    'DE': 'Germany',
    'FR': 'France',
    'ES': 'Spain',
    'IT': 'Italy',
    'JP': 'Japan',
    'KR': 'South Korea',
    'AU': 'Australia',
    'CA': 'Canada',
    'BR': 'Brazil',
    'IN': 'India',
    'RU': 'Russia',
  };

  return countryNames[code] || code;
}
